/* eslint-disable react/no-array-index-key */
import React from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import {
  Icon,
  List,
  ListItem,
  Paper,
} from '@material-ui/core';

import styles from '../styles.module.css';
import * as actions from '../../../../state';
import { controlsList as lineControls } from '../style-controls/lineControlLists';
import { controlsList as gaugeControls } from '../style-controls/gaugeControlList';

export const messages = defineMessages({
  CHARTS: {
    id: 'CHARTS',
    defaultMessage: 'Charts',
  },
  LINE_GRAPH: {
    id: 'LINE_GRAPH',
    defaultMessage: 'Line Chart',
  },
  BAR_GRAPH: {
    id: 'BAR_GRAPH',
    defaultMessage: 'Bar Chart',
  },
  PIE_CHART: {
    id: 'PIE_CHART',
    defaultMessage: 'Pie Chart',
  },
  HEATMAP: {
    id: 'HEATMAP',
    defaultMessage: 'Heatmap',
  },
  STATS: {
    id: 'STATS',
    defaultMessage: 'Stats',
  },
  STAT_BLOCK: {
    id: 'STAT_BLOCK',
    defaultMessage: 'Single Stat',
  },
  GAUGE: {
    id: 'GAUGE',
    defaultMessage: 'Gauge',
  },
  OTHER: {
    id: 'OTHER',
    defaultMessage: 'Other',
  },
  TABLE: {
    id: 'TABLE',
    defaultMessage: 'Table',
  },
  GRID: {
    id: 'GRID',
    defaultMessage: 'Grid',
  },
  NOTE: {
    id: 'NOTE',
    defaultMessage: 'Note',
  },
  NEW_NOTE: {
    id: 'NEW_NOTE',
    defaultMessage: 'New Note',
  },
  IFRAME: {
    id: 'IFRAME',
    defaultMessage: 'Iframe',
  },
  TYPE_SELECT_TOOLTIP: {
    id: 'TYPE_SELECT_TOOLTIP',
    defaultMessage: 'Choose how you would like to visualize your data.',
  },
});

export const chartTypes = [{
  group: 'Charts',
  labelId: 'CHARTS',
  types: [{
    type: 'ResponsiveLine',
    labelId: 'LINE_GRAPH',
    color: '#D32F2F',
    icon: 'show_chart',
  }, /* {
    type: 'ResponsiveBar',
    labelId: 'BAR_GRAPH',
    color: '#7B1FA2',
    icon: 'bar_chart',
    isDisabled: true,
  }, */{
    type: 'ResponsiveGauge',
    labelId: 'GAUGE',
    color: '#0288D1',
    nsIcon: 'nsicon-m-gauge-chart',
  }, /* {
    type: 'ResponsivePie',
    labelId: 'PIE_CHART',
    color: '#303F9F',
    icon: 'pie_chart',
    isDisabled: true,
  },{
    type: 'ResponsiveHeatmap',
    labelId: 'HEATMAP',
    color: '#00796B',
    icon: 'insert_chart',
    isDisabled: true,
  } */],
}, {
  group: 'Other',
  labelId: 'OTHER',
  types: [{
    type: 'ResponsiveStat',
    labelId: 'STAT_BLOCK',
    color: '#33691E',
    icon: 'looks_5',
  }, {
    type: 'ResponsiveTable',
    labelId: 'TABLE',
    color: '#F57F17',
    nsIcon: 'nsicon-m-table',
  }, {
    type: 'ResponsiveGridTable',
    labelId: 'GRID',
    color: '#FDD835',
    icon: 'border_all',
  }, {
    type: 'ResponsiveNote',
    labelId: 'NOTE',
    color: '#795548',
    icon: 'note_add',
  }, {
    type: 'ResponsiveIFrame',
    labelId: 'IFRAME',
    color: '#303F9F',
    icon: 'settings_system_daydream',
  }],
}];

function ChartTypeSelect({ intl }) {
  const dispatch = useDispatch();

  const selectedChart = useSelector(actions.readStateSelectedChart);

  /* Looks up the default values as defined in the chart's style controls */
  // eslint-disable-next-line class-methods-use-this
  const getDefaultChartProperties = (type) => {
    let controlList = [];
    const chartProps = {};

    switch (type) {
      case 'ResponsiveLine':
        controlList = lineControls;
        break;
      case 'ResponsiveGauge':
        controlList = gaugeControls;
        break;
      default:
    }

    controlList.forEach((control) => {
      const propNameParts = control.name.split('.'); // split props like "axisBottom.tickFormat.step"

      /* this is a super dumb way to construct a multilevel prop struct. will
           * support up to  3 levels of depth. this could be a recursive func.. */
      switch (propNameParts.length) {
        case 1:
          chartProps[propNameParts[0]] = control.default;
          break;
        case 2:
          if (!chartProps[propNameParts[0]]) { chartProps[propNameParts[0]] = {}; }
          chartProps[propNameParts[0]][propNameParts[1]] = control.default;
          break;
        case 3:
          if (!chartProps[propNameParts[0]]) { chartProps[propNameParts[0]] = {}; }
          if (!chartProps[propNameParts[0]][propNameParts[1]]) { chartProps[propNameParts[0]][propNameParts[1]] = {}; }
          chartProps[propNameParts[0]][propNameParts[1]][propNameParts[2]] = control.default;
          break;
        default:
      }
    });

    return chartProps;
  };

  const changeChartType = (chartType) => {
    const cloned = _.cloneDeep(selectedChart);
    cloned.type = chartType;
    cloned.properties = getDefaultChartProperties(chartType);
    cloned.data_sources = [];

    if (cloned.type === 'ResponsiveNote') {
      cloned.name = intl.formatMessage({ ...messages.NEW_NOTE });
    }

    dispatch(actions.updateSelectedChart(cloned));
  };

  return (
    <div className={styles.list}>
      <div className={styles.inlineTooltip}>
        <FormattedMessage {...messages.TYPE_SELECT_TOOLTIP} />
      </div>

      <List dense className={styles.menu} style={{ border: 'none' }}>
        <ListItem className={styles.typeContainer}>
          {chartTypes.map((chartGroup) => chartGroup.types.map((chartType, cTindex) => {
            let classes = styles.menuCard;
            if (selectedChart.type === chartType.type) {
              classes += ` ${styles.activeCard}`;
            }
            if (chartType.isDisabled) {
              classes += ` ${styles.disabledType}`;
            }
            return (
              <Paper
                key={cTindex}
                className={classes}
                elevation={0}
                style={{ backgroundColor: chartType.color }}
                onClick={() => changeChartType(chartType.type)}
              >
                {chartType.icon && (
                  <Icon className={styles.chartsIcons}>
                    {chartType.icon}
                  </Icon>
                )}
                {chartType.nsIcon && (
                  <Icon className={styles.chartsIcons} style={{ top: '8px !important' }}>
                    <span className={`nsicon-m ${chartType.nsIcon}`} />
                  </Icon>
                )}

                <p className={styles.menuTitle}>
                  <FormattedMessage {...messages[chartType.labelId]} />
                </p>

                <Icon className={`${styles.chartsCheck}`}>
                  check
                </Icon>
              </Paper>
            );
          }))}
        </ListItem>
      </List>
    </div>
  );
}

export default injectIntl(ChartTypeSelect);
